import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Bookings.css';

const CreateBooking = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: '',
    provider_id: '',
    service_id: '',
    scheduledDate: '',
    scheduledTime: '',
    customerNotes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, provRes, servRes] = await Promise.all([
          api.get(ENDPOINTS.CUSTOMERS.base, { params: { limit: 1000 } }),
          api.get(ENDPOINTS.PROVIDERS.base, { params: { limit: 1000 } }), // Removed status='approved' to show all
          api.get(ENDPOINTS.SERVICES.base, { params: { limit: 1000, isActive: true } })
        ]);

        // Helper to extract array from response
        const extractData = (res) => {
          if (Array.isArray(res)) return res;
          // Check for pagination wrapper: { data: [...], meta: ... }
          if (res.data && Array.isArray(res.data)) return res.data;
          // Check for nested pagination inside data: { data: { data: [...], meta: ... } }
          if (res.data && res.data.data && Array.isArray(res.data.data)) return res.data.data;
          return [];
        };

        const validCustomers = extractData(custRes);
        const validProviders = extractData(provRes);
        const validServices = extractData(servRes);

        console.log('Fetched Data:', { validCustomers, validProviders, validServices });

        setCustomers(validCustomers);
        setProviders(validProviders);
        setServices(validServices);
      } catch (error) {
        console.error('Failed to fetch dependency data:', error);
        toast.error('Failed to load required data. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.customer_id || !formData.provider_id || !formData.service_id || !formData.scheduledDate || !formData.scheduledTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        provider_id: parseInt(formData.provider_id),
        service_id: parseInt(formData.service_id)
      };

      console.log('Submitting booking:', payload);

      await api.post(ENDPOINTS.BOOKINGS.base, payload);
      toast.success('Booking created successfully!');
      navigate('/bookings');
    } catch (error) {
      console.error('Failed to create booking:', error);
      toast.error('Failed to create booking: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Loading booking resources...</p>
    </div>
  );

  return (
    <div className="create-booking-page">
      <div className="page-header">
        <button onClick={() => navigate('/bookings')} className="btn btn-secondary">
          <FaArrowLeft /> Back to Bookings
        </button>
      </div>

      <div className="content-card">
        <h3 className="card-title">Create New Booking</h3>

        <form onSubmit={handleSubmit} className="booking-form">
          <div className="form-group">
            <label>Customer *</label>
            <select name="customer_id" value={formData.customer_id} onChange={handleChange} className="form-control" required>
              <option value="">Select Customer</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.firstName} {c.lastName} ({c.email})</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Provider *</label>
            <select name="provider_id" value={formData.provider_id} onChange={handleChange} className="form-control" required>
              <option value="">Select Provider</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.firstName} {p.lastName} - {p.businessName || 'Freelance'}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Service *</label>
            <select name="service_id" value={formData.service_id} onChange={handleChange} className="form-control" required>
              <option value="">Select Service</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name} (${s.basePrice}/hr)</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Scheduled Date *</label>
              <input type="date" name="scheduledDate" value={formData.scheduledDate} onChange={handleChange} className="form-control" required key="date-input" />
            </div>
            <div className="form-group">
              <label>Scheduled Time *</label>
              <input type="time" name="scheduledTime" value={formData.scheduledTime} onChange={handleChange} className="form-control" required />
            </div>
          </div>

          <div className="form-group">
            <label>Customer Notes</label>
            <textarea name="customerNotes" value={formData.customerNotes} onChange={handleChange} className="form-control" rows="3" />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/bookings')}>Cancel</button>
            <button type="submit" className="btn btn-primary"><FaSave /> Create Booking</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBooking;
